import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ParentHeader from "../ParentHeader/ParentHeader";
import Loader from "../../Components/Loader/Loader";
import Help from "../../Components/Help/Help";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { useMaterialSymbols } from "../../Teacher/Oireachtas/components";
import { downloadPdf as downloadPdfFile } from "../../helper/DownloadHandlers";
import { getWithSession } from "./sessionRequests";
import moment from "moment";
import { dateLine, groupOf, groupsOf, shownComps, includesFolded, isEventDay } from "./oireachtasResultsModel";
import { CompRow, DancerCards, RecallNumbers, PlacesNumbers, RoundAwards, FullTable, DancerSheet } from "./OireachtasResultBlocks";
import compareComps from "../../Teacher/Oireachtas/compSort";
import "../../Teacher/Oireachtas/oireachtas.css";
import "./eventResults.css";
import "./oireachtasResults.css";

// The Oireachtas results pages (MC2 card f157ed22, Devin 2026-10-27), in the
// feis results design. Everything here is the published snapshot, and all of
// it is public (the official results show every dancer and every mark).
//   list         ?id=          tabs: My Results / Your students (signed in,
//                               once the organiser releases results to
//                               families), My adjudications (a judge), All Results
//   competition  ?id=&comp=    the recall numbers, or the full table
//   dancer       ?id=&comp=&card=   one dancer's page: place, every mark by
//                               round, top places
// Each level opens with a click and has a Back button. The only print is the
// competition's full table (the results-book PDF).
const BASE = () => process.env.REACT_APP_BASE_URL;

const OireachtasEventResults = () => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const eventId = params.get("id");
  const compId = params.get("comp");
  const card = params.get("card");
  const tabParam = params.get("tab");
  // a competition's own pages: the results (no view) or its round medals
  const view = params.get("view");

  const signedIn = !!localStorage.getItem("accessToken");
  const isTeacher = signedIn && localStorage.getItem("isTeacher") === "true";
  const isAdjudicator = signedIn && localStorage.getItem("isAdjudicator") === "true";

  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(null); // { event, competitions }
  const [family, setFamily] = useState(null); // { published, dancers } or null
  const [seats, setSeats] = useState({}); // compId -> Set("round-slot")
  const [details, setDetails] = useState({}); // compId -> payload | "error"
  const [q, setQ] = useState("");
  const [tq, setTq] = useState("");
  const [group, setGroup] = useState(null); // a type chip; null = the first (Solo championships)
  const [downloading, setDownloading] = useState(false);
  // event day: everything reloads every minute while the page is visible, as
  // on the feis results (tick counts the reloads; 0 = the first load)
  const [tick, setTick] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);

  // The event's published list, and for a signed-in visitor their dancers (a
  // family's, or a teacher's school's) and a judge's seats. A failed personal
  // read leaves the public page.
  // which event the state on screen belongs to: a minute refresh of that
  // event is quiet; anything else (a first load, another event) starts clean
  const loadedEvent = useRef(null);
  useEffect(() => {
    let stale = false;
    const quiet = tick > 0 && loadedEvent.current === eventId; // a refresh: no loader, a failed read keeps what is showing
    if (!quiet) {
      setLoading(true);
      if (loadedEvent.current !== eventId) {
        setIndex(null);
        setFamily(null);
        setSeats({});
        setDetails({});
        loadedEvent.current = eventId;
      }
    }
    (async () => {
      try {
        const res = await serviceConsumer("GET", `${BASE()}/oireachtas/${eventId}/results`);
        if (!stale) {
          setIndex(res);
          setUpdatedAt(new Date());
        }
      } catch (e) {
        if (!stale && !quiet) setIndex(null);
      }
      if (signedIn) {
        const [mine, adj] = await Promise.all([
          getWithSession(`${BASE()}/oireachtas/${eventId}/${isTeacher ? "school-results" : "my-results"}`).catch(() => null),
          isAdjudicator ? getWithSession(`${BASE()}/oireachtas/${eventId}/my-adjudications`).catch(() => null) : null,
        ]);
        // a quiet refresh keeps what it has when a read fails
        if (!stale && (mine || !quiet)) setFamily(mine && mine.published ? mine : null);
        if (!stale && (adj || !quiet)) {
          const map = {};
          (adj?.competitions || []).forEach((c) => {
            map[String(c.competition_id)] = new Set((c.seats || []).map((x) => `${x.round}-${x.slot}`));
          });
          setSeats(map);
        }
      }
      if (!stale) setLoading(false);
    })();
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, tick]);

  // Only the newest request for a competition may land: a navigation load and
  // a minute refresh can overlap, and an older reply (say the recall list)
  // must not replace newer final results.
  // A quiet refresh never overtakes a navigation load still on its way (its
  // failure would otherwise leave "Loading results…" until the next minute).
  const detailGen = useRef({});
  const detailPending = useRef({});
  const loadDetail = useCallback(async (id, quiet) => {
    const key = `${eventId}|${id}`;
    if (quiet && detailPending.current[key]) return;
    const gen = (detailGen.current[key] || 0) + 1;
    detailGen.current[key] = gen;
    if (!quiet) detailPending.current[key] = gen;
    try {
      const res = await serviceConsumer("GET", `${BASE()}/oireachtas/${eventId}/results/${id}`);
      if (detailGen.current[key] === gen) setDetails((p) => ({ ...p, [id]: res }));
    } catch (e) {
      // a failed reload keeps a table that is already showing, but a 404 means
      // the organiser withdrew the result: it leaves the screen, even on a
      // quiet reload (Fable review 2026-10-27)
      const gone = e?.error?.response?.status === 404;
      if (detailGen.current[key] !== gen) return;
      if (gone) setDetails((p) => ({ ...p, [id]: "error" }));
      else if (!quiet) setDetails((p) => (p[id] && p[id] !== "error" ? p : { ...p, [id]: "error" }));
    } finally {
      if (detailPending.current[key] === gen) delete detailPending.current[key];
    }
  }, [eventId]);
  useEffect(() => {
    if (compId) loadDetail(compId);
    window.scroll(0, 0);
    // view too: results <-> round medals is a new page (MC review a6f8407d R1-F3)
  }, [compId, card, view, loadDetail]);
  useEffect(() => {
    if (tick > 0 && compId) loadDetail(compId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  // The check runs every minute whatever the day, so a page opened the night
  // before starts reloading once the event day begins (and stops after).
  const daysRef = useRef(null);
  daysRef.current = index?.event?.days;
  const eventDay = isEventDay(index?.event?.days);
  const [, setClock] = useState(0);
  useEffect(() => {
    const bump = () => {
      if (document.visibilityState !== "visible") return;
      if (isEventDay(daysRef.current)) setTick((t) => t + 1);
      else setClock((c) => c + 1); // re-render so the live line follows the date
    };
    const timer = setInterval(bump, 60 * 1000);
    document.addEventListener("visibilitychange", bump);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", bump);
    };
  }, []);

  // Pages reached through this page's own links carry inApp, so Back knows the
  // previous history entry is the page they came from (a replace keeps the
  // entry's own flag).
  const go = (next, opts = {}) => {
    const p = new URLSearchParams();
    p.set("id", eventId);
    Object.entries(next).forEach(([k, v]) => v != null && v !== "" && p.set(k, v));
    const inApp = opts.replace ? !!location.state?.inApp : true;
    navigate(`${location.pathname}?${p.toString()}`, { ...opts, state: { ...(location.state || {}), inApp } });
  };
  // Back: to the page the visitor came from (as the feis results' Back), so a
  // dancer opened from My Results goes back to My Results. A competition or
  // dancer page opened from outside (a shared link) steps up a level instead,
  // replacing the entry, so Back can never bounce between two pages:
  // dancer -> competition -> list.
  // The router numbers this tab's history entries (history.state.idx, 0 for
  // the first page it loaded): above 0 there is a page of the site to go back to.
  const back = () => {
    const hasPrevious = (window.history.state?.idx ?? 0) > 0;
    if (location.state?.inApp && hasPrevious) return navigate(-1);
    if (card || view) return go({ comp: compId, tab: tabParam }, { replace: true });
    if (compId) return go({ tab: tabParam }, { replace: true });
    if (hasPrevious) return navigate(-1);
    return navigate("/results");
  };

  const event = index?.event;
  // the syllabus order (Devin 2026-10-30), not the codes' alphabet
  const comps = useMemo(() => [...(index?.competitions || [])].sort(compareComps), [index]);
  const published = comps.filter((c) => c.state === "results_published");
  const judged = published.filter((c) => seats[String(c._id)]);
  const familyDancers = useMemo(() => family?.dancers || [], [family]);
  const hasFamily = familyDancers.length > 0;
  const familyLabel = isTeacher ? "Your students" : "My Results";
  const tab = tabParam === "judged" && judged.length ? "judged"
    : tabParam === "mine" && hasFamily ? "mine"
      : tabParam === "all" ? "all"
        : hasFamily ? "mine" : "all";
  const showTabs = hasFamily || judged.length > 0;

  // the family's / school's cards in each competition (rows highlighted there)
  const mineByComp = useMemo(() => {
    const m = {};
    familyDancers.forEach((d) => d.results.forEach((r) => {
      const k = String(r.competition_id);
      (m[k] = m[k] || new Set()).add(String(r.card));
    }));
    return m;
  }, [familyDancers]);

  const { groups, grp, searching, list: allList } = shownComps(comps, group, q);
  // My adjudications gets the same type chips over the judge's own
  // competitions (one shared choice across tabs; Devin 2026-10-28)
  const judgedGroups = groupsOf(judged);
  const jgrp = judgedGroups.includes(group) ? group : judgedGroups[0];
  const judgedShown = judged.filter((c) => !jgrp || groupOf(c.type) === jgrp);
  // dancers with something published first; the rest are counted, not listed
  const withResults = familyDancers.filter((d) => d.results.length > 0);
  const waiting = familyDancers.length - withResults.length;
  const students = withResults.filter((d) => includesFolded(d.name, tq));

  const header = (
    <>
      <button type="button" className="er-back" onClick={back}><span className="msr" aria-hidden="true">chevron_left</span>Back</button>
      <div className="er-ph">
        {event?.logo ? <img className="er-logo" src={event.logo} alt="" /> : <div className="er-logo"><span className="msr" aria-hidden="true">emoji_events</span></div>}
        <div>
          <div className="or-eyebrow">Oireachtas results</div>
          <h1>{event?.name || "Results"}</h1>
          <div className="er-sub">
            {event?.eventDays?.length > 0 && <span><span className="msr" aria-hidden="true">calendar_today</span>{dateLine(event.eventDays)}</span>}
            {(event?.venue_city || event?.state) && <span><span className="msr" aria-hidden="true">location_on</span>{[event?.venue_city, event?.state].filter(Boolean).join(", ")}</span>}
          </div>
        </div>
      </div>
      {/* the whole width under the header, so it never wraps (Devin 2026-10-30) */}
      {eventDay && updatedAt && (
        <div className="er-live or-live"><span className="er-dot" />Results update automatically · Updated {moment(updatedAt).format("h:mm a")}</div>
      )}
    </>
  );

  const list = (
    <>
      {showTabs && (
        <div className="er-tabs" role="tablist">
          {hasFamily && (
            <button type="button" role="tab" aria-selected={tab === "mine"} className={`er-tab ${tab === "mine" ? "on" : ""}`} onClick={() => go({ tab: "mine" }, { replace: true })}>
              {familyLabel} <span className="er-tcount">{familyDancers.length}</span>
            </button>
          )}
          {judged.length > 0 && (
            <button type="button" role="tab" aria-selected={tab === "judged"} className={`er-tab ${tab === "judged" ? "on" : ""}`} onClick={() => go({ tab: "judged" }, { replace: true })}>
              My adjudications <span className="er-tcount">{judged.length}</span>
            </button>
          )}
          <button type="button" role="tab" aria-selected={tab === "all"} className={`er-tab ${tab === "all" ? "on" : ""}`} onClick={() => go({ tab: "all" }, { replace: true })}>
            All Results
          </button>
        </div>
      )}
      {tab === "mine" && (
        <>
          {isTeacher && (
            <div className="er-toolbar">
              <label className="er-search">
                <span className="msr" aria-hidden="true">search</span>
                <input aria-label="Find a student" placeholder="Find a student" value={tq} onChange={(e) => setTq(e.target.value)} />
              </label>
            </div>
          )}
          {students.length
            ? <DancerCards dancers={students} onOpen={(r) => go({ comp: r.competition_id, card: r.state === "results_published" ? r.card : null, tab })} />
            : <div className="er-empty">{withResults.length ? "No students match your search." : "Nothing published yet for your dancers."}</div>}
          {waiting > 0 && withResults.length > 0 && (
            <p className="or-legend">{waiting} more {isTeacher ? "student" : "dancer"}{waiting === 1 ? " has" : "s have"} nothing published yet.</p>
          )}
        </>
      )}
      {tab === "judged" && (
        <>
          {judgedGroups.length > 1 && (
            <div className="er-toolbar">
              <div className="er-chips" role="group" aria-label="Competition type">
                {judgedGroups.map((g) => (
                  <button key={g} type="button" className={`er-chip or-chip ${jgrp === g ? "on" : ""}`} aria-pressed={jgrp === g} onClick={() => setGroup(g)}>{g}</button>
                ))}
              </div>
            </div>
          )}
          <div className="or-list">
            {judgedShown.map((c) => <CompRow key={c._id} comp={c} onOpen={() => go({ comp: c._id, tab })} />)}
          </div>
        </>
      )}
      {tab === "all" && (
        <>
          <div className="er-toolbar">
            <label className="er-search">
              <span className="msr" aria-hidden="true">search</span>
              <input aria-label="Search competitions" placeholder="Competition name or code" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            {groups.length > 1 && (
              <div className="er-chips" role="group" aria-label="Competition type">
                {groups.map((g) => (
                  <button key={g} type="button" className={`er-chip or-chip ${grp === g && !searching ? "on" : ""}`} aria-pressed={grp === g && !searching} onClick={() => { setGroup(g); setQ(""); }}>{g}</button>
                ))}
              </div>
            )}
          </div>
          {allList.length ? (
            <div className="or-list">
              {allList.map((c) => {
                const mine = mineByComp[String(c._id)];
                const note = mine ? <span className="er-pill mine">{isTeacher ? `${mine.size} of your students` : "Your dancer"}</span> : null;
                return <CompRow key={c._id} comp={c} note={note} onOpen={() => go({ comp: c._id, tab })} />;
              })}
            </div>
          ) : (
            <div className="er-empty">{comps.length ? "No competitions match your search." : "No results published yet. Recall lists and results appear here as soon as the organiser publishes them."}</div>
          )}
        </>
      )}
    </>
  );

  const detail = compId ? details[compId] : null;
  const loaded = detail && detail !== "error" ? detail : null;
  const mineCards = (compId && mineByComp[compId]) || new Set();
  const printPdf = () =>
    downloadPdfFile(`${BASE()}/oireachtas/${eventId}/results/${compId}/results.pdf`, setDownloading, { name: `results-${loaded?.competition?.code || "competition"}` });

  const hasMedals = !!loaded && (loaded.roundAwards || []).some((l) => (l.winners || []).length);
  const compHead = loaded && (
    <div className="or-comphead">
      <div>
        <h2>{loaded.competition.name}<span className="dot"> · </span><span className="or-code">{loaded.competition.code}</span></h2>
      </div>
      {/* Devin 2026-10-30: the grid's PDF, and the round medals on a page of their own */}
      {(loaded.state === "results_published" || hasMedals) && !view && (
        <div className="or-headbtns">
          {loaded.state === "results_published" && (
            <button type="button" className="er-btn" onClick={printPdf} disabled={downloading}><span className="msr" aria-hidden="true">picture_as_pdf</span>{downloading ? "Preparing…" : "Full Grid PDF"}</button>
          )}
          {hasMedals && (
            <button type="button" className="er-btn" onClick={() => go({ comp: compId, tab: tabParam, view: "medals" })}><span className="msr" aria-hidden="true">military_tech</span>Round Medals</button>
          )}
        </div>
      )}
    </div>
  );

  const compPage = (
    <>
      {!detail && <div className="er-empty">Loading results…</div>}
      {detail === "error" && <div className="er-empty" role="alert">These results are not published, or could not be loaded.</div>}
      {loaded && (
        <>
          {compHead}
          {loaded.state === "recall_published"
            ? <RecallNumbers detail={loaded} mineCards={mineCards} />
            : loaded.state === "places_published"
              ? <PlacesNumbers detail={loaded} mineCards={mineCards} />
              : <div className="or-card or-tablecard"><FullTable detail={loaded} mineCards={mineCards} seats={seats[compId]} /></div>}
        </>
      )}
    </>
  );

  // the round medals on their own page (Devin 2026-10-30), not under the grid
  const medalsPage = (
    <>
      {!detail && <div className="er-empty">Loading results…</div>}
      {detail === "error" && <div className="er-empty" role="alert">These results are not published, or could not be loaded.</div>}
      {loaded && (
        <>
          {compHead}
          {hasMedals ? <RoundAwards detail={loaded} mineCards={mineCards} /> : <div className="er-empty">No round medals for this competition yet.</div>}
          <div className="or-foot">
            <button type="button" className="er-btn" onClick={() => go({ comp: compId, tab: tabParam })}><span className="msr" aria-hidden="true">table_view</span>{loaded.state === "results_published" ? "Full results" : "Recall list"}</button>
          </div>
        </>
      )}
    </>
  );

  const dancerRow = loaded?.rows?.find((r) => r.card === card);
  const dancerPage = (
    <>
      {!detail && <div className="er-empty">Loading results…</div>}
      {detail === "error" && <div className="er-empty" role="alert">These results are not published, or could not be loaded.</div>}
      {loaded && loaded.state !== "results_published" && <div className="er-empty">Final results for this competition aren&apos;t published yet.</div>}
      {loaded && loaded.state === "results_published" && (
        <>
          {dancerRow && (
            <div className="or-dancer">
              <h2>{dancerRow.name}</h2>
              <div className="or-dsub">#{dancerRow.card}{dancerRow.school ? ` · ${dancerRow.school}` : ""}</div>
              <div className="or-dcomp">{loaded.competition.name} · {loaded.competition.code}</div>
            </div>
          )}
          <DancerSheet detail={loaded} card={card} mineCards={mineCards} seats={seats[compId]} />
          <div className="or-foot">
            <button type="button" className="er-btn" onClick={() => go({ comp: compId, tab: tabParam })}><span className="msr" aria-hidden="true">table_view</span>Full results</button>
          </div>
        </>
      )}
    </>
  );

  return (
    <>
      {loading && <Loader />}
      <ParentHeader />
      <div className="oir er">
        {!loading && (
          <div className="er-wrap">
            {header}
            {!index && <div className="er-empty">These results could not be loaded.</div>}
            {index && !compId && list}
            {index && compId && !card && view !== "medals" && compPage}
            {index && compId && !card && view === "medals" && medalsPage}
            {index && compId && card && dancerPage}
          </div>
        )}
      </div>
      <Help />
    </>
  );
};

export default OireachtasEventResults;
